// =============================================
// VeriFactu Submission Service (SII / AEAT)
// Mandatory for Spain 2025-2026
// =============================================

import type {
  Invoice,
  InvoiceLine,
  TipoFactura,
  ClaveRegimen,
  TipoOperacion,
} from './types';

// SII Tipo Factura codes: F1=ordinaria, F2=simplificada, R1-R5=rectificativas
export type SIITipoFactura = 'F1' | 'F2' | 'R1' | 'R2' | 'R3' | 'R4' | 'R5';

// SII Clave Regimen: 01=general, 02=export, 07=criterio_caja, etc.
export type SIIClaveRegimen =
  | '01' | '02' | '03' | '04' | '05' | '06'
  | '07' | '08' | '09' | '14' | '15';

export type VeriFactuEstado = 'Correcto' | 'AceptadoConErrores' | 'Incorrecto';

export interface SIICabecera {
  IDVersionSii: string;
  Titular: { NombreRazon: string; NIF: string };
  TipoComunicacion: 'A0' | 'A1' | 'A4';
}

export interface SIIDesgloseIVA {
  TipoImpositivo: string;
  BaseImponible: string;
  CuotaRepercutida: string;
  CuotaRecargoEquivalencia?: string;
  TipoRecargoEquivalencia?: string;
}

export interface SIIPeriodoLiquidacion {
  Ejercicio: string;
  Periodo: string;
}

export interface SIIContraparte {
  NombreRazon: string;
  NIF?: string;
  IDOtro?: {
    CodigoPais?: string;
    IDType: '02' | '03' | '04' | '05' | '06' | '07';
    ID: string;
  };
}

export interface SIIFacturaEmitida {
  TipoFactura: SIITipoFactura;
  ClaveRegimenEspecialOTrascendencia: SIIClaveRegimen;
  ImporteTotal: string;
  DescripcionOperacion: string;
  Contraparte: SIIContraparte;
  TipoDesglose: {
    DesgloseFactura: {
      Sujeta: {
        NoExenta?: {
          TipoNoExenta: 'S1' | 'S2' | 'S3';
          DesgloseIVA: { DetalleIVA: SIIDesgloseIVA[] };
        };
        Exenta?: { BaseImponible: string; CausaExencion?: string };
      };
      NoSujeta?: { ImportePorArticulos7_14_Otros: string };
    };
  };
  FacturasRectificadas?: Array<{
    IDFacturaRectificada: {
      NumSerieFacturaEmisor: string;
      FechaExpedicionFacturaEmisor: string;
    };
  }>;
}

export interface VeriFactuRegistro {
  Cabecera: SIICabecera;
  RegistroLRFacturasEmitidas: {
    PeriodoLiquidacion: SIIPeriodoLiquidacion;
    IDFactura: {
      IDEmisorFactura: { NIF: string };
      NumSerieFacturaEmisor: string;
      FechaExpedicionFacturaEmisor: string;
    };
    FacturaExpedida: SIIFacturaEmitida;
  };
}

export interface VeriFactuSubmissionResult {
  success: boolean;
  csv?: string;
  estado: VeriFactuEstado;
  errores?: Array<{ codigo: string; descripcion: string }>;
  registroRespuesta?: {
    CSV: string;
    CodigoErrorRegistro?: string;
    DescripcionErrorRegistro?: string;
    EstadoRegistro: string;
  };
}

// F1=ordinaria, F2=simplificada, R1=rectificativa default
export function mapTipoFacturaSII(tipo: TipoFactura): SIITipoFactura {
  switch (tipo) {
    case 'ordinaria': return 'F1';
    case 'simplificada': return 'F2';
    case 'rectificativa': return 'R1';
    case 'proforma':
    case 'presupuesto':
    case 'albaran':
      return 'F1';
    default: {
      const _exhaustive: never = tipo;
      return 'F1';
    }
  }
}

export function mapRegimenSII(regimen: ClaveRegimen): SIIClaveRegimen {
  switch (regimen) {
    case 'general': return '01';
    case 'recargo_equivalencia': return '01';
    case 'simplificado': return '01';
    case 'criterio_caja': return '07';
    default: {
      const _exhaustive: never = regimen;
      return '01';
    }
  }
}

function mapTipoNoExenta(operacion: TipoOperacion): 'S1' | 'S2' | 'S3' {
  switch (operacion) {
    case 'nacional': return 'S1';
    case 'intracomunitaria': return 'S1';
    case 'exportacion': return 'S1';
    case 'inversion_sujeto_pasivo': return 'S2';
    default: return 'S1';
  }
}

function formatDateSII(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}-${month}-${year}`;
}

function formatDecimalSII(value: number): string {
  return value.toFixed(2);
}

export function buildVeriFactuRegistro(
  invoice: Invoice,
  lines: InvoiceLine[],
  emisorNIF: string,
  emisorNombre: string,
  tipoComunicacion: 'A0' | 'A1' | 'A4' = 'A0'
): VeriFactuRegistro {
  const fechaEmision = new Date(invoice.fecha_emision);
  const ejercicio = fechaEmision.getFullYear().toString();
  const periodo = String(fechaEmision.getMonth() + 1).padStart(2, '0');

  // Build IVA desglose grouped by tax rate
  const ivaMap = new Map<string, { base: number; cuota: number; recargo: number; recargoTipo: number }>();

  for (const line of lines) {
    if (line.es_suplido) continue;
    const key = `${line.porcentaje_iva}`;
    const existing = ivaMap.get(key) || { base: 0, cuota: 0, recargo: 0, recargoTipo: 0 };
    existing.base += line.base_linea;
    existing.cuota += line.cuota_iva;
    if (line.recargo_cuota) {
      existing.recargo += line.recargo_cuota;
      existing.recargoTipo = line.recargo_porcentaje || 0;
    }
    ivaMap.set(key, existing);
  }

  const detalleIVA: SIIDesgloseIVA[] = Array.from(ivaMap.entries()).map(
    ([tipoImpositivo, data]) => {
      const detalle: SIIDesgloseIVA = {
        TipoImpositivo: formatDecimalSII(parseFloat(tipoImpositivo)),
        BaseImponible: formatDecimalSII(data.base),
        CuotaRepercutida: formatDecimalSII(data.cuota),
      };
      if (data.recargo > 0) {
        detalle.TipoRecargoEquivalencia = formatDecimalSII(data.recargoTipo);
        detalle.CuotaRecargoEquivalencia = formatDecimalSII(data.recargo);
      }
      return detalle;
    }
  );

  const contraparte: SIIContraparte = { NombreRazon: invoice.cliente_nombre };
  if (invoice.cliente_nif && /^[A-Z0-9]{8,9}[A-Z]?$/i.test(invoice.cliente_nif)) {
    contraparte.NIF = invoice.cliente_nif;
  } else if (invoice.cliente_nif) {
    contraparte.IDOtro = { IDType: '04', ID: invoice.cliente_nif };
  }

  const facturaExpedida: SIIFacturaEmitida = {
    TipoFactura: mapTipoFacturaSII(invoice.tipo),
    ClaveRegimenEspecialOTrascendencia: mapRegimenSII(invoice.clave_regimen),
    ImporteTotal: formatDecimalSII(invoice.total_factura),
    DescripcionOperacion: `Factura ${invoice.numero}`,
    Contraparte: contraparte,
    TipoDesglose: {
      DesgloseFactura: {
        Sujeta: {
          NoExenta: {
            TipoNoExenta: mapTipoNoExenta(invoice.tipo_operacion),
            DesgloseIVA: { DetalleIVA: detalleIVA },
          },
        },
      },
    },
  };

  if (invoice.tipo === 'rectificativa' && invoice.factura_rectificada_id) {
    facturaExpedida.FacturasRectificadas = [{
      IDFacturaRectificada: {
        NumSerieFacturaEmisor: invoice.factura_rectificada_id,
        FechaExpedicionFacturaEmisor: formatDateSII(invoice.fecha_emision),
      },
    }];
  }

  return {
    Cabecera: {
      IDVersionSii: '1.1',
      Titular: { NombreRazon: emisorNombre, NIF: emisorNIF },
      TipoComunicacion: tipoComunicacion,
    },
    RegistroLRFacturasEmitidas: {
      PeriodoLiquidacion: { Ejercicio: ejercicio, Periodo: periodo },
      IDFactura: {
        IDEmisorFactura: { NIF: emisorNIF },
        NumSerieFacturaEmisor: invoice.numero,
        FechaExpedicionFacturaEmisor: formatDateSII(invoice.fecha_emision),
      },
      FacturaExpedida: facturaExpedida,
    },
  };
}

/**
 * STUB: Submit VeriFactuRegistro to AEAT SII endpoint.
 * Uses env var VERIFACTU_API_URL. Logs request, returns mock success.
 */
export async function submitToVeriFactu(
  registro: VeriFactuRegistro
): Promise<VeriFactuSubmissionResult> {
  const apiUrl = process.env.VERIFACTU_API_URL || 'https://prewww1.aeat.es/wlpl/SSII-FACT/ws/fe/SiiFactFEV2SOAP';

  console.log('[verifactu-service] STUB: submitToVeriFactu called', {
    apiUrl,
    emisorNIF: registro.Cabecera.Titular.NIF,
    numFactura: registro.RegistroLRFacturasEmitidas.IDFactura.NumSerieFacturaEmisor,
    tipoFactura: registro.RegistroLRFacturasEmitidas.FacturaExpedida.TipoFactura,
    importeTotal: registro.RegistroLRFacturasEmitidas.FacturaExpedida.ImporteTotal,
    tipoComunicacion: registro.Cabecera.TipoComunicacion,
  });

  const mockCSV = `CSV${Date.now()}${Math.random().toString(36).substring(2, 8).toUpperCase()}`;

  return {
    success: true,
    csv: mockCSV,
    estado: 'Correcto',
    registroRespuesta: { CSV: mockCSV, EstadoRegistro: 'Correcto' },
  };
}

/**
 * STUB: Check status of previously submitted invoice.
 * Uses env var VERIFACTU_API_URL.
 */
export async function checkVeriFactuStatus(
  emisorNIF: string,
  numFactura: string,
  fechaExpedicion: string
): Promise<VeriFactuSubmissionResult> {
  const apiUrl = process.env.VERIFACTU_API_URL || 'https://prewww1.aeat.es/wlpl/SSII-FACT/ws/fe/SiiFactFEV2SOAP';

  console.log('[verifactu-service] STUB: checkVeriFactuStatus called', {
    apiUrl, emisorNIF, numFactura, fechaExpedicion,
  });

  return {
    success: true,
    estado: 'Correcto',
    csv: `CSV_CHECK_${Date.now()}`,
    registroRespuesta: { CSV: `CSV_CHECK_${Date.now()}`, EstadoRegistro: 'Correcto' },
  };
}
